import type { FilterSchema } from '@/shared/components/filter/validation'

import { map, pipe, prop, propOr, sum } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useProductCountByEngineStatisticsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'

export default function NumericReadyForOptimizationWidget({
	className,
	commonFilters,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const { data: productsCountByEngine, isLoading } =
		useProductCountByEngineStatisticsQuery({
			filters: [...commonFilters],
			currency,
		})
	const productCount = computeProductCount(productsCountByEngine) ?? 0
	const revenue = computeRevenue(productsCountByEngine ?? [])
	const pricePointsPercent = computePricePointsPercent(
		productsCountByEngine ?? []
	)

	return (
		<Widget className={cn('relative flex flex-auto flex-col', className)}>
			<WidgetHeader
				title={t('widget.ready_for_opt.title')}
				tooltip={t('widget.ready_for_opt.tooltip')}
				subtitle={t('widget.ready_for_opt.subtitle')}
			/>
			<WidgetContent className='truncate pt-0'>
				{isLoading ? (
					<Skeleton className='col-span-2 h-10 w-32' />
				) : (
					<div className='flex flex-col space-y-1'>
						<div className='flex items-center space-x-2'>
							<span className='truncate text-xs text-muted-foreground'>
								{formatNumber(productCount)}{' '}
								{t('general_skus', { defaultValue: 'SKUs' })}
							</span>
						</div>
						<span className='flex items-center truncate text-3xl font-semibold'>
							{formatMoney(revenue, currency, NumberFormats.Compact)}
							<Badge size='sm' className='ml-2'>
								{formatNumber(pricePointsPercent, NumberFormats.Percent)}
							</Badge>
						</span>
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
const computePricePointsPercent = pipe([
	map(propOr(0, 'price_points_percent')),
	sum,
])
const computeRevenue = pipe([map(propOr(0, 'revenue')), sum])
const computeProductCount = pipe(map(prop('product_count')), sum)
